import { EditNote, KeyboardArrowUp } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Address } from "../../model/Address";
import { AddressAutocomplete } from "./AddressAutocomplete";
import StateInputForm from "./StateInputForm";
interface AddressFormProps {
  current?: Address;
  required?: boolean;
  autocompleteLabel?: string;
  noValidate?: boolean;
  condensed?: boolean;
  manual?: boolean;
  noControls?: boolean;
  canExpand?: boolean;
  strict?: boolean;
  disabled?: boolean;
  autoSubmit?: boolean;
  variant?: "standard" | "filled" | "outlined";
  autocompleteSize?: "small" | "medium";
  onSubmit?: (address: Address) => void;
  onChange?: (address: Address) => void;
  onCancel?: () => void;
  onInsufficient?: () => void;
}

/**
 * A reusable form used to retrieve an address from the user.
 *
 * Methods to retrieve address:
 * @param onSubmit Returns the new address from the user when they press submit.
 * @param onChange Returns the new address from the user every time they update.
 * @param onCancel Enables the cancel button and assigns functionality.
 * @param onInsufficient This is called when the autocomplete selects an address that has no matching google place.
 *
 * Properties:
 * @param current Autofills the form with the set address.
 * @param autocompleteLabel The label for the autocomplete
 * @param noValidate Disables validation preventing sumission of the form.
 * @param condensed Disables the manual form.
 * @param manual Disables the autocomplete form.
 * @param canExpand Enables the ability for the user to exit condensed mode
 * @param noControls Hides the control buttons.
 * @param autoSubmit Setting to true sets form to submit as soon as autocomplete sends a value
 * @param strict Disables the ability to search for address by business name
 * @param required Does not effect functionality, just adds the asterisk.
 * @param disabled Disables the address form if set to true.
 */
const AddressForm: FC<AddressFormProps> = ({
  variant,
  current,
  onChange,
  ...props
}) => {
  const initialAddress: Address = {
    id: "",
    first_line: "",
    second_line: "",
    city: "",
    state: "",
    zip_code: 0,
  };

  const [address, setAddress] = useState<Address>(current ?? initialAddress);
  const [errorText, setErrorText] = useState("");
  const [condensedCurrent, setCondensedCurrent] = useState(
    props.condensed || false,
  );

  useEffect(() => {
    setCondensedCurrent(props.condensed || false);
  }, [props.condensed]);

  useEffect(() => {
    if (current) {
      setAddress(current);
      onChange?.(current);
    }
  }, [current, onChange]);

  const isAddressEmpty = (addr: Address) => {
    return (
      !addr.first_line &&
      !addr.second_line &&
      !addr.city &&
      !addr.state &&
      (!addr.zip_code || addr.zip_code === 0)
    );
  };

  const validateAddress = (addr: Address): string | null => {
    const firstLineRegex = /^[A-Za-z0-9][A-Za-z0-9 ]*$/;
    const cityStateRegex = /^[A-Za-z0-9 ]+$/;
    const zipCodeRegex = /^\d+$/;
    if (!firstLineRegex.test(addr.first_line) || addr.first_line === "") {
      return "Cannot submit address: First line is invalid.";
    }
    if (!cityStateRegex.test(addr.state) || addr.state === "") {
      return "Cannot submit address: State is invalid.";
    }
    if (!cityStateRegex.test(addr.city) || addr.city === "") {
      return "Cannot submit address: City is invalid.";
    }
    if (!zipCodeRegex.test(addr.zip_code.toString()) || addr.zip_code === 0) {
      return "Cannot submit address: Zip code is invalid.";
    }
    return null;
  };

  const onSubmit = () => {
    setErrorText("");
    if (isAddressEmpty(address)) return;
    if (!props.noValidate) {
      const error = validateAddress(address);
      if (error) {
        setErrorText(error);
        return;
      }
    }
    props.onSubmit?.(address);
  };

  const autoSubmit = (value?: Address) => {
    const toSubmit = value ?? address;
    if (isAddressEmpty(toSubmit)) return;
    if (
      !toSubmit.first_line ||
      !toSubmit.city ||
      !toSubmit.state ||
      !toSubmit.zip_code
    ) {
      return;
    }
    props.onSubmit?.(toSubmit);
  };

  const handleAddressChange = (
    changes: Partial<Address>,
    opts?: { submit?: boolean },
  ) => {
    setAddress((prev) => {
      const updated = { ...prev, ...changes };
      onChange?.(updated);
      if (opts?.submit && props.autoSubmit) autoSubmit(updated);
      return updated;
    });
  };

  const handleCancel = () => props.onCancel?.();

  return (
    <>
      <Grid container p={0} spacing={1} direction={"row"}>
        <Grid size={12}>
          {!props.manual && (
            <Grid
              container
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              size={12}
            >
              <Grid flexGrow={1}>
                {props.disabled ? (
                  <TextField
                    required={props.required}
                    color="primary"
                    size={props.autocompleteSize}
                    label={props.autocompleteLabel}
                    variant={variant || "outlined"}
                    fullWidth
                  />
                ) : (
                  <AddressAutocomplete
                    strict={props.strict}
                    size={props.autocompleteSize}
                    required={props.required}
                    label={props.autocompleteLabel}
                    variant={variant}
                    onInsufficient={props.onInsufficient}
                    condensed={condensedCurrent}
                    onSubmit={(address) => {
                      if (
                        (!address || isAddressEmpty(address)) &&
                        !condensedCurrent
                      )
                        return;
                      if (!address || isAddressEmpty(address)) {
                        setAddress(initialAddress);
                        if (onChange) onChange(initialAddress);
                        if (props.autoSubmit) autoSubmit();
                        return;
                      }
                      setAddress(address);
                      if (onChange && !props.autoSubmit) onChange(address);
                      if (props.onSubmit && props.autoSubmit)
                        props.onSubmit(address);
                    }}
                    value={address}
                  />
                )}
              </Grid>
              {props.canExpand && (
                <Grid
                  minWidth={"55px"}
                  container
                  flexDirection={"column"}
                  alignItems="center"
                  size={1}
                >
                  <Tooltip
                    title={
                      condensedCurrent
                        ? "Manually update address"
                        : "Hide address form"
                    }
                    sx={{
                      transform:
                        variant === "outlined" ||
                        variant === "filled" ||
                        !variant
                          ? "translateY(11px)"
                          : " ",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setCondensedCurrent(!condensedCurrent)}
                    >
                      {condensedCurrent ? <EditNote /> : <KeyboardArrowUp />}
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
        {!condensedCurrent && (
          <>
            <Grid size={12}>
              <TextField
                fullWidth
                required={props.required}
                value={address?.first_line || ""}
                size="small"
                id="line-one"
                name="address-line1"
                label="Address Line One"
                variant="filled"
                autoComplete="address-line1"
                onChange={(event) =>
                  handleAddressChange(
                    { first_line: event.target.value },
                    { submit: true },
                  )
                }
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                value={address?.second_line || ""}
                size="small"
                id="line-two"
                name="address-line2"
                label="Address Line Two"
                variant="filled"
                autoComplete="address-line2"
                onChange={(event) =>
                  handleAddressChange(
                    { second_line: event.target.value },
                    { submit: true },
                  )
                }
              />
            </Grid>
            <Grid container spacing={1} rowSpacing={0}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  required={props.required}
                  value={address?.city || ""}
                  size="small"
                  id="city"
                  name="address-level2"
                  label="City"
                  variant="filled"
                  autoComplete="address-level2"
                  onChange={(event) =>
                    handleAddressChange(
                      { city: event.target.value },
                      { submit: true },
                    )
                  }
                />
              </Grid>
              <Grid size={4}>
                <StateInputForm
                  value={address?.state}
                  textFieldProps={{
                    variant: "filled",
                    size: "small",
                  }}
                  onChange={(event) =>
                    handleAddressChange({ state: event?.abbreviation })
                  }
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  required={props.required}
                  inputMode="numeric"
                  inputProps={{ type: "number" }}
                  value={
                    address && address?.zip_code == 0 ? "" : address?.zip_code
                  }
                  size="small"
                  id="zip"
                  name="postal-code"
                  label="Zip Code"
                  variant="filled"
                  autoComplete="postal-code"
                  onChange={(event) =>
                    handleAddressChange(
                      { zip_code: +event.target.value },
                      { submit: true },
                    )
                  }
                />
              </Grid>
            </Grid>
          </>
        )}
        <Grid>
          <Typography color={"error"}>{errorText}</Typography>
        </Grid>
        {!props.noControls && (
          <Grid container spacing={1} size={12}>
            <Grid>
              <Button
                disabled={
                  address?.first_line == "" ||
                  address?.city == "" ||
                  address?.state == "" ||
                  address?.zip_code == 0
                }
                onClick={onSubmit}
                variant="contained"
              >
                Submit
              </Button>
            </Grid>
            {props.onCancel && (
              <Grid>
                <Button onClick={handleCancel} variant="outlined">
                  Cancel
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default AddressForm;
